import React, { ReactNode, useRef, useState } from "react"
import {
  makeStyles,
  createStyles,
  ITheme,
  useOnClickOutside
} from "@chainsafe/common-theme"
import { Typography } from "@chainsafe/common-components"
import clsx from "clsx"
import { CaretDown } from "phosphor-react"

const useStyles = makeStyles(
  ({ constants, animation, typography, palette, overrides }: ITheme) =>
    createStyles({
      // JSS in CSS goes here
      root: {
        display: "flex",
        position: "relative",
        background: "#292929",
        borderRadius: "8px",
        border: `1px solid #292929`,
        marginRight: "10px",
        "&.open": {},
        ...overrides?.MenuDropdown?.root
      },
      title: {
        fontWeight: 600,
        fontSize: "18px",
        cursor: "pointer",
        padding: `${constants.generalUnit * .8}px ${constants.generalUnit*1.7}px`,
        flexDirection: "row",
        alignItems: "center",
        "& p": {
          position: "relative",
          "& ~ * svg": {
            marginLeft: constants.generalUnit
          }
        },
        ...overrides?.MenuDropdown?.title
      },
      icon: {
        fontSize: "unset",
        height: 22,
        width: 20,
        padding: 0,
        position: "relative",
        // Can create animation variant here
        "&.none": {
          "& svg": {
            transform: "translateY(-50%)"
          }
        },
        "&.flip": {
          "&": {
            transform: "translateY(0%) rotateX(0deg)"
          },
          "&.open": {
            transform: "translateY(-5%) rotateX(180deg)"
          },
          ...overrides?.MenuDropdown?.icon?.flip
        },
        "&.rotate": {
          "& svg": {
            transform: "translateY(-50%) rotateZ(0deg)"
          },
          "&.open svg": {
            transform: "translateY(-50%) rotateZ(180deg)"
          },
          ...overrides?.MenuDropdown?.icon?.rotate
        },
        "& svg": {
          height: 14,
          width: 14,
          top: "50%",
          left: 0,
          position: "absolute",
          transitionDuration: `${animation.transform}ms`
        },
        ...overrides?.MenuDropdown?.icon?.root
      },
      options: {
        minWidth: "100%",
        backgroundColor: "#262626",
        height: 0,
        overflow: "hidden",
        visibility: "hidden",
        opacity: 0,
        transitionDuration: `${animation.transform}ms`,
        zIndex: 1000,
        padding: 0,
        marginTop: "5px",
        "&.bottom-center": {
          top: "100%",
          left: "50%",
          transform: "translateX(-50%)",
          ...overrides?.MenuDropdown?.options?.position?.bottomCenter
        },
        "&.bottom-right": {
          top: "100%",
          right: 0,
          ...overrides?.MenuDropdown?.options?.position?.bottomRight
        },
        position: "absolute",
        "&.open": {
          height: "auto",
          opacity: 1,
          visibility: "visible",
          ...overrides?.MenuDropdown?.options?.open
        },
        ...overrides?.MenuDropdown?.options?.root
      },
      item: {
        fontFamily: "DM Sans",
        cursor: "pointer",
        ...typography.h5,
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "center",
        padding: `${constants.generalUnit * 1.5}px ${
          constants.generalUnit * 2
        }px`,
        color: palette.additional["gray"][1],
        transitionDuration: `${animation.transform}ms`,
        backgroundColor: "initial",
        "&:hover": {
          color: palette.primary.main,
          background: "#181818",
          ...overrides?.MenuDropdown?.item?.hover
        },
        "& > *:first-child ~ *": {
          marginLeft: constants.generalUnit / 2
        },
        "& > svg": {
          transitionDuration: `${animation.transform}ms`,
          fill: palette.additional["gray"][7]
        },
        ...overrides?.MenuDropdown?.item?.root
      }
    })
)

interface IMenuItem {
  contents: ReactNode | ReactNode[]
  onClick?: () => void
}

interface IMenuDropdownProps {
  className?: string
  autoclose?: boolean
  indicator?: any
  animation?: "rotate" | "flip" | "none"
  anchor?:
    | "top-left"
    | "top-center"
    | "top-right"
    | "bottom-left"
    | "bottom-center"
    | "bottom-right"
  menuItems: IMenuItem[]
  title?: string
  classNames?: {
    icon?: string
    options?: string
    item?: string
    title?: string
    titleText?: string
  }
  testId?: string
}

const CustomMenu = ({
  className,
  menuItems,
  autoclose = true,
  anchor = "bottom-center",
  indicator = CaretDown,
  animation = "flip",
  title,
  classNames,
  testId
}: IMenuDropdownProps) => {
  const Icon = indicator
  const classes = useStyles()
  const [open, setOpen] = useState<boolean>(false)

  const ref = useRef(null)
  useOnClickOutside(ref, () => {
    if (open) {
      setOpen(false)
    }
  })
  return (
    <div
      ref={ref}
      className={clsx(classes.root, className)}
    >
      <section
        data-testid={`dropdown-title-${testId}`}
        onClick={() => setOpen(!open)}
        className={clsx(classes.title, classNames?.title, {
          open
        })}
      >
        {title && (
          <Typography
            component="p"
            variant="h5"
            className={classNames?.titleText}
          >
            {title}
          </Typography>
        )}
        <Icon
          className={clsx(classes.icon, animation, classNames?.icon, {
            open
          })}
        />
      </section>
      <div
        className={clsx(classes.options, classNames?.options, anchor, {
          open
        })}
      >
        {menuItems.map((item: IMenuItem, index: number) => (
          <div
            data-testid={`dropdown-item-${testId}`}
            key={`menu-${index}`}
            className={clsx(classes.item, classNames?.item)}
            onClick={() => {
              autoclose && setOpen(false)
              item.onClick && item.onClick()
            }}
          >
            {item.contents}
          </div>
        ))}
      </div>
    </div>
  )
}

export default CustomMenu
